// Ubicación en el repo: src/components/PatientFormModal.jsx
// Modal Crear/Editar paciente (spec-pacientes-agenda.md 1.3). Mismo
// componente para ambos casos: si `patient` viene con datos, es edición;
// si viene null, es creación.
import { useEffect, useState } from 'react';
import {
  createPatient,
  updatePatient,
  findPatientByRut,
  sanitizeRut as sanitizeRutRpc,
} from '../lib/patients';
import { formatPatientName } from '../lib/format';
import { printConsentDocument } from '../lib/consentDocument';

const SEXO_OPTS = ['Masculino', 'Femenino', 'No binario', 'Prefiere no informar'];
const PREVISION_OPTS = ['Fonasa', 'Isapre', 'Particular', 'Otra'];

function emptyForm(defaultBranchId) {
  return {
    nombre: '',
    segundo_nombre: '',
    apellido: '',
    segundo_apellido: '',
    rut: '',
    birthdate: '',
    phone: '+569',
    email: '',
    sexo: '',
    prevision: '',
    prevision_detalle: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
    consent_given: false,
    branchIds: defaultBranchId ? [defaultBranchId] : [],
  };
}

function patientToForm(patient) {
  return {
    nombre: patient.nombre ?? '',
    segundo_nombre: patient.segundo_nombre ?? '',
    apellido: patient.apellido ?? '',
    segundo_apellido: patient.segundo_apellido ?? '',
    rut: patient.rut ?? '',
    birthdate: patient.birthdate ?? '',
    phone: patient.phone ?? '+569',
    email: patient.email ?? '',
    sexo: patient.sexo ?? '',
    prevision: patient.prevision ?? '',
    prevision_detalle: patient.prevision_detalle ?? '',
    address: patient.address ?? '',
    emergency_contact_name: patient.emergency_contact_name ?? '',
    emergency_contact_phone: patient.emergency_contact_phone ?? '',
    notes: patient.notes ?? '',
    consent_given: patient.consent_given ?? false,
    branchIds: patient.branch_ids ?? [],
  };
}

export default function PatientFormModal({ patient, branches, defaultBranchId, onClose, onSaved, actorId }) {
  const isEdit = !!patient;
  const [form, setForm] = useState(() =>
    isEdit ? patientToForm(patient) : emptyForm(defaultBranchId)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rutChecking, setRutChecking] = useState(false);
  const [rutDuplicate, setRutDuplicate] = useState(null); // { id, nombre, apellido }

  useEffect(() => {
    setForm(isEdit ? patientToForm(patient) : emptyForm(defaultBranchId));
    setRutDuplicate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleBranch(branchId) {
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(branchId)
        ? f.branchIds.filter((id) => id !== branchId)
        : [...f.branchIds, branchId],
    }));
  }

  async function handleRutBlur() {
    setRutDuplicate(null);
    if (!form.rut.trim()) return;
    setRutChecking(true);
    try {
      const clean = await sanitizeRutRpc(form.rut);
      const existing = await findPatientByRut(clean, isEdit ? patient.id : undefined);
      setRutDuplicate(existing);
    } catch {
      // No bloqueamos el formulario si el chequeo previo falla por red —
      // el índice único del backend igual protege contra el duplicado.
    } finally {
      setRutChecking(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.nombre.trim() || !form.apellido.trim()) {
      setError('Nombre y apellido son obligatorios.');
      return;
    }
    // Un paciente sin ninguna sucursal asignada queda invisible en el
    // listado para todos (ver DECISIONS.md #14, pendiente relacionado) —
    // se exige acá para que ese estado ya no pueda ocurrir desde la UI.
    if (form.branchIds.length === 0) {
      setError('Selecciona al menos una sucursal.');
      return;
    }
    if (rutDuplicate) {
      setError(
        `Ese RUT ya está registrado para ${formatPatientName(rutDuplicate)}. No se puede guardar duplicado.`
      );
      return;
    }

    setSaving(true);
    try {
      const originalConsentDate = isEdit ? patient.consent_date : null;
      const consentDate = originalConsentDate || (form.consent_given ? new Date().toISOString() : null);

      // Campos cortos identificadores en MAYÚSCULAS al guardar (no solo
      // visualmente): nombre/apellido/dirección/contacto de emergencia,
      // igual que branches.name ya hacía. A propósito NO se tocan
      // email/rut/teléfono/notas (texto libre largo) — decisión de
      // Nicolás, 19-ago-2026.
      const values = {
        nombre: form.nombre.trim().toUpperCase(),
        segundo_nombre: form.segundo_nombre.trim().toUpperCase() || null,
        apellido: form.apellido.trim().toUpperCase(),
        segundo_apellido: form.segundo_apellido.trim().toUpperCase() || null,
        rut: form.rut.trim() || null,
        birthdate: form.birthdate || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        sexo: form.sexo || null,
        prevision: form.prevision || null,
        prevision_detalle:
          form.prevision === 'Isapre' ? form.prevision_detalle.trim().toUpperCase() || null : null,
        address: form.address.trim().toUpperCase() || null,
        emergency_contact_name: form.emergency_contact_name.trim().toUpperCase() || null,
        emergency_contact_phone: form.emergency_contact_phone.trim() || null,
        notes: form.notes.trim() || null,
        consent_given: form.consent_given,
        consent_date: consentDate,
      };

      if (isEdit) {
        await updatePatient(patient.id, values, form.branchIds, actorId);
      } else {
        await createPatient(values, form.branchIds, actorId);
      }
      onSaved();
    } catch (err) {
      if (err.code === '23505') {
        setError('Ese RUT ya está registrado en otro paciente.');
      } else {
        setError(err.message || 'No se pudo guardar el paciente.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar paciente' : 'Nuevo paciente'}</h2>

        <form onSubmit={handleSubmit} className="patient-form">
          <div className="form-row">
            <label className="field">
              <span>Nombre *</span>
              <input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} required />
            </label>
            <label className="field">
              <span>Segundo nombre</span>
              <input
                value={form.segundo_nombre}
                onChange={(e) => set('segundo_nombre', e.target.value)}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Apellido *</span>
              <input value={form.apellido} onChange={(e) => set('apellido', e.target.value)} required />
            </label>
            <label className="field">
              <span>Segundo apellido</span>
              <input
                value={form.segundo_apellido}
                onChange={(e) => set('segundo_apellido', e.target.value)}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>RUT</span>
              <input value={form.rut} onChange={(e) => set('rut', e.target.value)} onBlur={handleRutBlur} />
              {rutChecking && <span className="field-hint">Revisando…</span>}
              {rutDuplicate && (
                <span className="field-hint field-hint--error">
                  Ya registrado: {formatPatientName(rutDuplicate)}
                </span>
              )}
            </label>
            <label className="field">
              <span>Fecha de nacimiento</span>
              <input
                type="date"
                value={form.birthdate}
                onChange={(e) => set('birthdate', e.target.value)}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Teléfono</span>
              <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </label>
            <label className="field">
              <span>Email</span>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Sexo</span>
              <select value={form.sexo} onChange={(e) => set('sexo', e.target.value)}>
                <option value="">—</option>
                {SEXO_OPTS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Previsión</span>
              <select value={form.prevision} onChange={(e) => set('prevision', e.target.value)}>
                <option value="">—</option>
                {PREVISION_OPTS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {form.prevision === 'Isapre' && (
            <label className="field">
              <span>¿Cuál Isapre?</span>
              <input
                value={form.prevision_detalle}
                onChange={(e) => set('prevision_detalle', e.target.value)}
              />
            </label>
          )}

          <label className="field">
            <span>Dirección</span>
            <input value={form.address} onChange={(e) => set('address', e.target.value)} />
          </label>

          <div className="form-row">
            <label className="field">
              <span>Contacto de emergencia — nombre</span>
              <input
                value={form.emergency_contact_name}
                onChange={(e) => set('emergency_contact_name', e.target.value)}
              />
            </label>
            <label className="field">
              <span>Contacto de emergencia — teléfono</span>
              <input
                value={form.emergency_contact_phone}
                onChange={(e) => set('emergency_contact_phone', e.target.value)}
              />
            </label>
          </div>

          <div className="field">
            <span>Sucursal(es)</span>
            <div className="checkbox-group">
              {branches.map((b) => (
                <label key={b.id} className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={form.branchIds.includes(b.id)}
                    onChange={() => toggleBranch(b.id)}
                  />
                  {b.name}
                </label>
              ))}
            </div>
          </div>

          <label className="field">
            <span>Notas / antecedentes clínicos</span>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </label>

          <div className="consent-row">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={form.consent_given}
                onChange={(e) => set('consent_given', e.target.checked)}
              />
              Consentimiento informado firmado
            </label>
            {/* Para un paciente nuevo (todavía sin ficha) este es el único lugar
                donde se puede imprimir el consentimiento en el momento del
                registro. Para uno ya existente, se imprime desde la ficha
                (PatientDetail.jsx) — tiene más sentido ahí que acá. */}
            {!isEdit && (
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  const branchNamesText = branches
                    .filter((b) => form.branchIds.includes(b.id))
                    .map((b) => b.name)
                    .join(', ');
                  printConsentDocument(form, branchNamesText);
                }}
              >
                Generar e imprimir documento
              </button>
            )}
          </div>

          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving || rutChecking}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
